import { useState, useRef } from 'react';
import { 
  Download, 
  Trash2, 
  Upload, 
  Plus, 
  X, 
  Edit2,
  Save,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  File,
  Moon,
  Sun,
  AlertTriangle,
  CheckCircle,
  Info,
  Home,
  LogOut,
  Settings
} from 'lucide-react';
import axios from 'axios';

export default function Metaflyse() {
  const [darkMode, setDarkMode] = useState(true);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [metadataFields, setMetadataFields] = useState([]);
  const [newMetaKey, setNewMetaKey] = useState('');
  const [newMetaValue, setNewMetaValue] = useState('');
  const [editingMetadata, setEditingMetadata] = useState(null);
  const [notification, setNotification] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState(null);
  const fileInputRef = useRef(null);

  const APP_VERSION = '1.0.0';

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
    //console.log('Notification:', { type, message });
  };

  const handleFileUpload = async (e) => {
    const uploadedFiles = e.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    const file = uploadedFiles[0]; // Single file upload for simplicity
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) return 90;
          return prev + 5;
        });
      }, 100);

      setIsAnalyzing(true);
      setMetadataLoading(true);
      setMetadataError(null);

      const response = await axios.post('api/inflyse/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      clearInterval(interval);
      setUploadProgress(100);
      setTimeout(() => {
        setUploadProgress(null);
        setIsAnalyzing(false);
      }, 500);

      const fileData = response.data;
      const newFile = {
        id: fileData.id,
        name: fileData.name,
        size: fileData.size,
        type: fileData.type,
        lastModified: new Date(fileData.lastModified).toLocaleString(),
        metadata: fileData.metadata,
        hiddenFiles: fileData.hiddenFiles || [],
        downloadUrl: fileData.downloadUrl,
        previewUrl: URL.createObjectURL(file),
      };

      setFiles(prev => [...prev, newFile]);
      setSelectedFile(newFile);
      setMetadataFields(Object.entries(newFile.metadata).map(([key, value]) => ({ key, value })));
      setMetadataLoading(false);
      showNotification('success', 'File uploaded and analyzed successfully');
      console.log('File uploaded:', newFile);
    } catch (error) {
      //console.error('Upload error:', error);
      showNotification('error', error.response?.data?.error || 'Failed to upload file');
      setUploadProgress(null);
      setIsAnalyzing(false);
      setMetadataLoading(false);
      setMetadataError(error.response?.data?.error || 'Failed to load metadata');
    }
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setMetadataFields(Object.entries(file.metadata).map(([key, value]) => ({ key, value })));
    setMetadataLoading(false);
    setMetadataError(null);
    //console.log('Selected file:', file.name);
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`api/inflyse/file/${fileId}`);
      setFiles(prev => {
        const updated = prev.filter(file => file.id !== fileId);
        updated.forEach(file => URL.revokeObjectURL(file.previewUrl));
        return updated;
      });
      if (selectedFile && selectedFile.id === fileId) {
        setSelectedFile(null);
        setMetadataFields([]);
      }
      showNotification('info', 'File deleted');
      //console.log('Deleted file:', fileId);
    } catch (error) {
      //console.error('Delete error:', error);
      showNotification('error', 'Failed to delete file');
    }
  };

  const addMetadataField = () => {
    if (!newMetaKey.trim() || !selectedFile) return;
    const updatedFields = [...metadataFields, { key: newMetaKey, value: newMetaValue }];
    setMetadataFields(updatedFields);
    setNewMetaKey('');
    setNewMetaValue('');
    showNotification('success', 'Metadata added locally');
    //console.log('Added metadata:', { key: newMetaKey, value: newMetaValue });
  };

  const deleteMetadataField = (keyToDelete) => {
    if (!selectedFile) return;
    const updatedFields = metadataFields.filter(field => field.key !== keyToDelete);
    setMetadataFields(updatedFields);
    showNotification('info', 'Metadata removed locally');
    //console.log('Deleted metadata:', keyToDelete);
  };

  const startEditMetadata = (key, value) => {
    setEditingMetadata({ key, value });
    //console.log('Editing metadata:', { key, value });
  };

  const saveMetadataEdit = async () => {
    if (!editingMetadata || !selectedFile) return;

    const updatedFields = metadataFields.map(field => 
      field.key === editingMetadata.key ? { ...field, value: editingMetadata.value } : field
    );
    setMetadataFields(updatedFields);

    try {
      const metadata = Object.fromEntries(updatedFields.map(field => [field.key, field.value]));
      const response = await axios.put(`api/inflyse/file/${selectedFile.id}/metadata`, { metadata });
      const updatedFile = response.data;
      setFiles(prev => prev.map(f => {
        if (f.id === selectedFile.id) {
          URL.revokeObjectURL(f.previewUrl);
          return { ...f, metadata: updatedFile.metadata, downloadUrl: updatedFile.downloadUrl, previewUrl: URL.createObjectURL(new Blob([f.previewUrl])) };
        }
        return f;
      }));
      setSelectedFile({ ...selectedFile, metadata: updatedFile.metadata, downloadUrl: updatedFile.downloadUrl });
      setEditingMetadata(null);
      showNotification('success', 'Metadata updated and file modified');
      console.log('Updated metadata:', metadata);
    } catch (error) {
      console.error('Metadata update error:', error);
      showNotification('error', 'Failed to update metadata');
      setMetadataError('Failed to update metadata');
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await axios.get(`api/inflyse/file/${fileId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotification('success', 'File downloaded');
      console.log('Downloaded file:', fileId);
    } catch (error) {
      console.error('Download error:', error);
      showNotification('error', 'Failed to download file');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <ImageIcon className="w-6 h-6" />;
    if (fileType.startsWith('audio/')) return <Music className="w-6 h-6" />;
    if (fileType.startsWith('video/')) return <Video className="w-6 h-6" />;
    if (fileType === 'application/pdf') return <FileText className="w-6 h-6" />;
    return <File className="w-6 h-6" />;
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload({ target: { files: e.dataTransfer.files } });
    }
  };

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    console.log('Toggled theme:', !darkMode ? 'dark' : 'light');
  };

   const handleHomeClick = () => {
    // handle Home button click
    window.location.href = '/'; 
  };

  const handleSettingsClick = () => {
    // handle Settings button click
    console.log('Settings clicked');
  };

  const handleLogout = async () => {
  try {
    const response = await fetch('/d-auth/logout', {
      method: 'POST', // or 'GET', depending on your backend implementation
      credentials: 'include', // include cookies with the request
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {       
      
      window.location.href = '/'; 
    } else {
      // If there's an error during logout
      const errorData = await response.json();
      //console.error('Logout failed', errorData);
      alert( errorData ||'Logout failed. Please try again.');
    }
  } catch (error) {
    //console.error('Error during logout:', error);
    alert('An error occurred. Please try again.');
  }
};


  const themeClass = darkMode ? 'bg-gradient-to-br from-gray-900 to-indigo-950 text-gray-100' : 'bg-gradient-to-br from-gray-100 to-indigo-100 text-gray-900';
  const cardClass = darkMode ? 'bg-gray-800/80 border-indigo-700/50 backdrop-blur-md' : 'bg-white/80 border-indigo-200/50 backdrop-blur-md';
  const inputClass = darkMode ? 'bg-gray-700/50 border-indigo-600/50 text-white focus:ring-indigo-500' : 'bg-gray-100/50 border-indigo-300/50 text-gray-900 focus:ring-indigo-400';
  const buttonPrimaryClass = darkMode ? 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500' : 'bg-indigo-500 hover:bg-indigo-600 focus:ring-indigo-400';
  const buttonSecondaryClass = darkMode ? 'bg-gray-700/50 hover:bg-gray-600/50 focus:ring-indigo-500' : 'bg-gray-200/50 hover:bg-gray-300/50 focus:ring-indigo-400';

  return (
    <div className={`min-h-screen ${themeClass} transition-colors duration-300 font-sans`}>
      {/* Navigation Bar */}
      <nav className={`sticky top-0 z-50 ${cardClass} border-b shadow-lg px-6 py-4 flex items-center justify-between`}>
        <div className="flex items-center gap-4">
          <img src="https://canto-wp-media.s3.amazonaws.com/app/uploads/2020/04/19185944/metadata-1.jpg" alt="Inflyse Logo" className="h-10 w-10 rounded-full" />
          <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent">
            Metaflyse
          </h1>
        </div>
        <div className="flex items-center gap-6">
          <button
            className="flex items-center gap-2 text-gray-300 hover:text-indigo-400 transition"
            onClick={handleHomeClick}
          >
            <Home className="w-5 h-5" /> Home
          </button>
          {/*<button
            className="flex items-center gap-2 text-gray-300 hover:text-indigo-400 transition"
            onClick={handleSettingsClick}
          >
            <Settings className="w-5 h-5" /> Settings
          </button>*/}
          <button
            className="flex items-center gap-2 text-gray-300 hover:text-indigo-400 transition"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5" /> Logout
          </button>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full ${buttonSecondaryClass} transition`}
            aria-label="Toggle theme"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto p-6">
        {/* Notification */}
        {notification && (
          <div 
            className={`fixed top-16 right-6 p-4 rounded-lg shadow-xl flex items-center gap-3 z-50 transition-all duration-300 
            ${notification.type === 'success' ? 'bg-green-600/90' : 
              notification.type === 'error' ? 'bg-red-600/90' : 
              'bg-blue-600/90'} text-white backdrop-blur-sm`}
          >
            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
             notification.type === 'error' ? <AlertTriangle className="w-5 h-5" /> :
             <Info className="w-5 h-5" />}
            <p className="font-medium">{notification.message}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* File Upload Area */}
          <div className={`col-span-1 ${cardClass} rounded-2xl border shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl`}>
            <div className="p-6 border-b border-indigo-700/50">
              <h2 className="text-xl font-semibold flex items-center gap-2 bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent">
                <Upload className="w-5 h-5" /> Upload Files
              </h2>
            </div>
            <div 
              className={`p-8 flex flex-col items-center justify-center min-h-[250px] relative
                ${dragActive ? 'bg-indigo-900/20 border-2 border-indigo-500/50 rounded-lg' : ''} transition-all duration-200`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              {uploadProgress !== null ? (
                <div className="w-full">
                  <div className="w-full bg-gray-700/50 rounded-full h-3 mb-4">
                    <div 
                      className="bg-indigo-500 h-3 rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-center font-medium">Uploading... {uploadProgress}%</p>
                </div>
              ) : isAnalyzing ? (
                <div className="flex flex-col items-center">
                  <svg className="animate-spin h-10 w-10 text-indigo-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="font-medium text-indigo-300">Analyzing metadata...</p>
                </div>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="text-center">
                    <div className="mb-4 inline-flex p-6 rounded-full bg-indigo-600/10">
                      <Upload className="w-10 h-10 text-indigo-400" />
                    </div>
                    <h3 className="font-semibold mb-2 text-lg">Drop files here or click to upload</h3>
                    <p classNameremark="text-sm text-gray-400">Supports images, audio, video, PDFs (max 50MB)</p>
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className={`mt-4 px-6 py-2 rounded-lg ${buttonPrimaryClass} text-white font-medium shadow-md hover:shadow-lg transition-all duration-200`}
                    >
                      Select File
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* File List */}
            <div className="p-6 border-t border-indigo-700/50">
              <h3 className="font-semibold mb-3 text-lg text-indigo-300">Uploaded Files</h3>
              {files.length === 0 ? (
                <p className="text-gray-500 text-sm italic">No files uploaded</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-indigo-600">
                  {files.map(file => (
                    <div 
                      key={file.id} 
                      className={`p-3 rounded-lg flex items-center justify-between cursor-pointer
                        ${selectedFile && selectedFile.id === file.id ? 
                          darkMode ? 'bg-indigo-900/30' : 'bg-indigo-100' : 
                          'hover:bg-indigo-700/20'} transition-all duration-200`}
                      onClick={() => handleFileSelect(file)}
                    >
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.type)}
                        <div>
                          <p className="truncate max-w-[150px] font-medium" title={file.name}>{file.name}</p>
                          <p className="text-xs text-gray-400">{formatBytes(file.size)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(file.id, file.name);
                          }}
                          className="p-1 hover:bg-green-500/20 rounded-full transition"
                        >
                          <Download className="w-4 h-4 text-green-400" />
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFile(file.id);
                          }}
                          className="p-1 hover:bg-red-500/20 rounded-full transition"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* File Details & Metadata */}
          <div className={`col-span-2 ${cardClass} rounded-2xl border shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl`}>
            {selectedFile ? (
              <div className="h-full flex flex-col">
                <div className="p-6 border-b border-indigo-700/50">
                  <h2 className="text-xl font-semibold flex items-center gap-2 bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent">
                    <FileText className="w-5 h-5" /> File Details
                  </h2>
                </div>
                
                {/* File Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
                  <div className={`col-span-1 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100/50'} p-4 rounded-lg`}>
                    {/* File Preview */}
                    <div className="mb-4">
                      {selectedFile.type.startsWith('image/') && (
                        <img src={selectedFile.previewUrl} alt="Preview" className="w-full h-32 object-cover rounded-lg shadow-md" />
                      )}
                      {selectedFile.type.startsWith('audio/') && (
                        <audio controls src={selectedFile.previewUrl} className="w-full" />
                      )}
                      {selectedFile.type.startsWith('video/') && (
                        <video controls src={selectedFile.previewUrl} className="w-full h-32 object-cover rounded-lg shadow-md" />
                      )}
                      {selectedFile.type === 'application/pdf' && (
                        <iframe src={selectedFile.previewUrl} className="w-full h-32 rounded-lg shadow-md" title="PDF Preview" />
                      )}
                      {!['image/', 'audio/', 'video/', 'application/pdf'].some(t => selectedFile.type.startsWith(t)) && (
                        <div className="flex items-center justify-center h-32 bg-gray-700/50 rounded-lg">
                          <p className="text-gray-400 text-sm">Preview not available</p>
                        </div>
                      )}
                    </div>
                    <h3 className="text-center font-semibold mb-4 truncate text-indigo-300" title={selectedFile.name}>
                      {selectedFile.name}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Size:</span>
                        <span>{formatBytes(selectedFile.size)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Type:</span>
                        <span>{selectedFile.type || 'Unknown'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Modified:</span>
                        <span>{selectedFile.lastModified}</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex justify-center gap-2">
                      <button 
                        onClick={() => handleDownload(selectedFile.id, selectedFile.name)}
                        className={`px-4 py-1 rounded-lg flex items-center gap-1 ${buttonSecondaryClass} font-medium shadow-md hover:shadow-lg transition`}
                      >
                        <Download className="w-4 h-4" /> Download
                      </button>
                      <button 
                        onClick={() => handleDeleteFile(selectedFile.id)}
                        className="px-4 py-1 rounded-lg flex items-center gap-1 bg-red-600/20 text-red-400 hover:bg-red-600/30 font-medium shadow-md hover:shadow-lg transition"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                    {selectedFile.hiddenFiles?.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-yellow-400">Hidden Files Detected:</h4>
                        <ul className="text-xs text-gray-400 mt-1">
                          {selectedFile.hiddenFiles.map((hf, i) => (
                            <li key={i}>{hf.name} ({formatBytes(hf.size)})</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {/* Metadata Edit */}
                  <div className="col-span-2">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg text-indigo-300">Metadata</h3>
                      <div className="text-xs text-gray-400">{metadataFields.length} properties</div>
                    </div>
                    
                    {metadataLoading ? (
                      <div className="flex flex-col items-center justify-center h-64">
                        <svg className="animate-spin h-10 w-10 text-indigo-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="font-medium text-indigo-300">Loading metadata...</p>
                      </div>
                    ) : metadataError ? (
                      <div className="flex flex-col items-center justify-center h-64 text-red-400">
                        <AlertTriangle className="w-10 h-10 mb-4" />
                        <p className="font-medium">{metadataError}</p>
                      </div>
                    ) : (
                      <>
                        {/* Add new metadata field */}
                        <div className="mb-6 grid grid-cols-5 gap-2">
                          <input 
                            type="text" 
                            placeholder="Property name"
                            value={newMetaKey}
                            onChange={(e) => setNewMetaKey(e.target.value)}
                            className={`col-span-2 px-4 py-2 rounded-lg border ${inputClass} shadow-sm focus:outline-none focus:ring-2 transition`}
                          />
                          <input 
                            type="text" 
                            placeholder="Value"
                            value={newMetaValue}
                            onChange={(e) => setNewMetaValue(e.target.value)}
                            className={`col-span-2 px-4 py-2 rounded-lg border ${inputClass} shadow-sm focus:outline-none focus:ring-2 transition`}
                          />
                          <button 
                            onClick={addMetadataField}
                            className={`flex items-center justify-center ${buttonPrimaryClass} text-white rounded-lg shadow-md hover:shadow-lg transition`}
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {/* Metadata list */}
                        <div className="space-y-2 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-indigo-600 pr-2">
                          {metadataFields.length > 0 ? (
                            metadataFields.map((field, index) => (
                              <div 
                                key={index} 
                                className={`${darkMode ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-100/50 hover:bg-gray-200'} 
                                  p-3 rounded-lg flex items-center justify-between transition-all duration-200 shadow-sm`}
                              >
                                {editingMetadata && editingMetadata.key === field.key ? (
                                  <div className="flex-1 flex gap-2">
                                    <div className="font-medium text-indigo-300">{field.key}:</div>
                                    <input
                                      type="text"
                                      value={editingMetadata.value}
                                      onChange={(e) => setEditingMetadata({ ...editingMetadata, value: e.target.value })}
                                      className={`flex-1 px-3 py-1 rounded-lg ${inputClass} shadow-sm focus:outline-none focus:ring-2 transition`}
                                    />
                                  </div>
                                ) : (
                                  <div className="flex-1">
                                    <span className="font-medium text-indigo-300">{field.key}:</span> <span className="text-gray-300">{field.value}</span>
                                  </div>
                                )}
                                
                                <div className="flex gap-1">
                                  {editingMetadata && editingMetadata.key === field.key ? (
                                    <button 
                                      onClick={saveMetadataEdit}
                                      className="p-1 hover:bg-green-500/20 rounded-full transition"
                                    >
                                      <Save className="w-4 h-4 text-green-400" />
                                    </button>
                                  ) : (
                                    <button 
                                      onClick={() => startEditMetadata(field.key, field.value)}
                                      className="p-1 hover:bg-blue-500/20 rounded-full transition"
                                    >
                                      <Edit2 className="w-4 h-4 text-blue-400" />
                                    </button>
                                  )}
                                  <button 
                                    onClick={() => deleteMetadataField(field.key)}
                                    className="p-1 hover:bg-red-500/20 rounded-full transition"
                                  >
                                    <X className="w-4 h-4 text-red-400" />
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-center py-4">No metadata available</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <FileText className="w-16 h-16 text-indigo-400 mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-indigo-300">No File Selected</h3>
                <p className="text-gray-400 max-w-md">
                  Upload a file to preview, analyze, and edit its metadata with Inflyse
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`mt-12 ${cardClass} border-t shadow-lg px-6 py-4 text-center`}>
        <p className="text-gray-400 text-sm">
          &copy; {new Date().getFullYear()} Inflyse. All rights reserved.
        </p>
        <p className="text-gray-500 text-xs mt-1">
          Inflyse v{APP_VERSION} - Advanced Metadata Analysis Platform
        </p>
      </footer>
    </div>
  );
}
